// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { FreezeHoleCell, FreezePlayerStanding, FreezeSection } from './types';

export type FreezeRow = FreezePlayerStanding & { name: string };

// Standings rows in backend rank order, with display names resolved.
export function buildFreezeRows(
  section: FreezeSection | null,
  nameLookup: Map<string, string>,
  fallbackName: string,
): FreezeRow[] {
  if (!section) return [];
  return section.standings.map((standing) => ({
    ...standing,
    name: nameLookup.get(standing.playerId) ?? fallbackName,
  }));
}

export function findPlayerFreeze(
  section: FreezeSection | null,
  playerId: string | null | undefined,
): FreezePlayerStanding | null {
  if (!section || !playerId) return null;
  return section.standings.find((standing) => standing.playerId === playerId) ?? null;
}

export function getLockableHoles(holes: FreezeHoleCell[]): FreezeHoleCell[] {
  return holes.filter((hole) => hole.lockable && !hole.locked);
}

// Any player whose window is open — anyone in the round may lock for others.
export function findOpenWindowStandings(section: FreezeSection | null): FreezePlayerStanding[] {
  if (!section || section.isFinal) return [];
  return section.standings.filter((standing) => standing.window.isOpen);
}

// Mid-tour display ranking: only locked points count until the final result,
// so re-rank rows by lockedPoints with shared positions on ties.
export function rankByLockedPoints<T extends { lockedPoints: number; position: number }>(rows: T[]): T[] {
  const ordered = [...rows].sort((a, b) => b.lockedPoints - a.lockedPoints);
  let lastPoints: number | null = null;
  let lastPosition = 1;
  return ordered.map((row, index) => {
    const position = row.lockedPoints === lastPoints ? lastPosition : index + 1;
    lastPoints = row.lockedPoints;
    lastPosition = position;
    return { ...row, position };
  });
}

// A snapshot of Freeze standings as they stood after the given round. The
// backend section is always the tour's current (possibly later) state, so the
// round-summary screen uses this to show the round being viewed instead of
// always the live result — e.g. round 2 of 3 should not read "final round
// underway" just because round 3 has since started. A hole already locked
// with a source round at or before the cutoff stays locked; anything else
// (not locked yet, or locked using a later round than the cutoff) is shown as
// still-open, filled provisionally from the latest eligible round, same as a
// currently-open hole. Falls back to the unscoped section when the round id
// is missing or not part of the tour's Freeze rounds.
export function buildFreezeSectionThroughRound(
  section: FreezeSection | null,
  roundId: string | null | undefined
): FreezeSection | null {
  if (!section) return null;
  const target = roundId?.trim().toLowerCase();
  if (!target) return section;
  const roundIds = section.rounds.map((round) => round.roundId.trim().toLowerCase());
  const cutoffIndex = roundIds.indexOf(target);
  if (cutoffIndex < 0) return section;
  const roundIndexByRoundId = new Map(roundIds.map((id, index) => [id, index]));
  const includedRoundIds = new Set(roundIds.slice(0, cutoffIndex + 1));
  const isLastRound = cutoffIndex === roundIds.length - 1;

  const rescored = section.standings.map((standing) => {
    let lockedPoints = 0;
    let unlockedPoints = 0;
    let lockedHoleCount = 0;
    const holes = standing.holes.map((hole) => {
      const lockSourceIndex = hole.sourceRoundId
        ? roundIndexByRoundId.get(hole.sourceRoundId.trim().toLowerCase()) ?? -1
        : -1;
      if (hole.locked && lockSourceIndex >= 0 && lockSourceIndex <= cutoffIndex) {
        lockedPoints += hole.points ?? 0;
        lockedHoleCount += 1;
        return hole;
      }

      const eligible = hole.pointsByRound.filter((entry) =>
        includedRoundIds.has(entry.roundId.trim().toLowerCase())
      );
      if (eligible.length === 0) {
        return { ...hole, locked: false, points: null, sourceRoundId: null, stale: false };
      }
      const latest = eligible.reduce((best, entry) => {
        const entryIndex = roundIndexByRoundId.get(entry.roundId.trim().toLowerCase()) ?? -1;
        const bestIndex = roundIndexByRoundId.get(best.roundId.trim().toLowerCase()) ?? -1;
        return entryIndex > bestIndex ? entry : best;
      });
      unlockedPoints += latest.points;
      return { ...hole, locked: false, points: latest.points, sourceRoundId: latest.roundId, stale: false };
    });
    return {
      ...standing,
      holes,
      lockedPoints,
      unlockedPoints,
      lockedHoleCount,
      totalPoints: lockedPoints + unlockedPoints,
    };
  });

  // Ranked by total, matching the backend's own ranking — the card itself
  // re-ranks by locked points alone whenever the result isn't final.
  const ordered = [...rescored].sort((a, b) => b.totalPoints - a.totalPoints);
  let lastPoints: number | null = null;
  let lastPosition = 1;
  const standings = ordered.map((standing, index) => {
    const position = standing.totalPoints === lastPoints ? lastPosition : index + 1;
    lastPoints = standing.totalPoints;
    lastPosition = position;
    return { ...standing, position };
  });

  return {
    ...section,
    isFinal: section.isFinal && isLastRound,
    isFinalRound: isLastRound,
    standings,
  };
}

export type FreezeHoleStatus = {
  // The hole is locked in: its points are banked and can never change.
  frozen: boolean;
  // Points banked (when frozen) or still pending from an earlier tour round —
  // null when the player has nothing on this hole yet.
  points: number | null;
};

// Freeze standing per player for one hole, as it looks while playing the given
// round: whether the hole is already locked and the points behind it. Pending
// points come from the most recent round *before* this one, which is what the
// player would lock. Empty for the tour's first round (nothing banked yet) or
// for an unknown round id, so the badge stays off until round two.
export function buildFreezeStatusBeforeRound(
  section: FreezeSection | null,
  holeNumber: number,
  currentRoundId: string | null | undefined,
): Map<string, FreezeHoleStatus> {
  const statusByPlayerId = new Map<string, FreezeHoleStatus>();
  const current = currentRoundId?.trim().toLowerCase();
  if (!section || !current) return statusByPlayerId;
  const roundIds = section.rounds.map((round) => round.roundId.trim().toLowerCase());
  const currentIndex = roundIds.indexOf(current);
  if (currentIndex < 1) return statusByPlayerId;
  const priorRoundIds = roundIds.slice(0, currentIndex);
  section.standings.forEach((standing) => {
    const hole = standing.holes.find((cell) => cell.holeNumber === holeNumber);
    if (!hole) return;
    if (hole.locked) {
      statusByPlayerId.set(standing.playerId, { frozen: true, points: hole.points });
      return;
    }
    const pointsByRoundId = new Map(
      hole.pointsByRound.map((entry) => [entry.roundId.trim().toLowerCase(), entry.points]),
    );
    const pending = priorRoundIds
      .filter((roundId) => pointsByRoundId.has(roundId))
      .map((roundId) => pointsByRoundId.get(roundId) as number);
    statusByPlayerId.set(standing.playerId, {
      frozen: false,
      points: pending.length > 0 ? pending[pending.length - 1] : null,
    });
  });
  return statusByPlayerId;
}

export type FreezeSelectionTotals = {
  lockedTotal: number;
  lockedCount: number;
  selectedCount: number;
  selectedPoints: number;
  projectedLockedTotal: number;
  // Locked + selected points per locked + selected hole; null before anything
  // is locked or selected. Recomputes as the selection changes.
  projectedAverage: number | null;
};

// Totals for the lock screen: what is already locked, what the current
// selection would add, and the locked total after confirming.
export function computeSelectionTotals(
  holes: FreezeHoleCell[],
  selectedHoleNumbers: ReadonlySet<number>,
): FreezeSelectionTotals {
  let lockedTotal = 0;
  let lockedCount = 0;
  let selectedPoints = 0;
  let selectedCount = 0;
  holes.forEach((hole) => {
    if (hole.locked) {
      lockedTotal += hole.points ?? 0;
      lockedCount += 1;
      return;
    }
    if (selectedHoleNumbers.has(hole.holeNumber) && hole.lockable) {
      selectedCount += 1;
      selectedPoints += hole.points ?? 0;
    }
  });
  const projectedHoleCount = lockedCount + selectedCount;
  return {
    lockedTotal,
    lockedCount,
    selectedCount,
    selectedPoints,
    projectedLockedTotal: lockedTotal + selectedPoints,
    projectedAverage: projectedHoleCount > 0 ? (lockedTotal + selectedPoints) / projectedHoleCount : null,
  };
}
